<script lang="ts">
  import { getUserId } from '~/auth/utils';
  import { useSupabaseClient } from '~/lib/SupabaseProvider.svelte';
  import { navHistory } from '~/lib/nav.svelte';
  import { page } from '$app/state';
  import type { Snippet } from 'svelte';

  type Props = {
    onClick?: (e: MouseEvent) => void | undefined | Promise<void | undefined>;
    class?: string;
    disabled?: boolean;
    iconOnly?: boolean;
    selected?: boolean;
    type?: 'submit';
    form?: string;
    href?: string;
    outlink?: boolean;
    button?: HTMLButtonElement;
    buttonId?: string;
    replaceState?: boolean;
    children: Snippet;
  };

  let { button = $bindable(), ...props }: Props = $props();

  const supabaseClient = useSupabaseClient();

  const handleClick = async (e: MouseEvent) => {
    props.onClick?.(e);
    const b = e.currentTarget as HTMLElement;
    if (b.nodeName === 'A') {
      // push nav history
      navHistory.current.push({
        url: page.url.toString(),
        action: props.replaceState ? 'replace' : 'push',
        state: page.state,
        timestamp: new Date(),
      });
    }
    if (props.buttonId) {
      supabaseClient.from('click_analytics').insert({
        userId: getUserId(),
        buttonId: props.buttonId,
      });
    }
    // TODO
    // if (href && !outlink) {
    //   e.preventDefault();
    //   console.log('replacing');
    //   replaceState(href, {});
    // }
  };
</script>

{#if props.href}
  <a
    href={props.href}
    id={props.buttonId}
    style:pointer-events={props.disabled ? 'none' : 'all'}
    onclick={handleClick}
    class="action {props.class}"
    class:selected={props.selected}
    class:icon-only={props.iconOnly}
    target={(props.outlink ?? true) ? '_blank' : undefined}
    rel={(props.outlink ?? true) ? 'noreferrer' : undefined}
    data-sveltekit-replacestate={props.replaceState}
  >
    {@render props.children()}
  </a>
{:else}
  <button
    id={props.buttonId}
    onclick={handleClick}
    bind:this={button}
    type={props.type}
    disabled={props.disabled}
    form={props.form}
    class="action {props.class}"
    class:selected={props.selected}
    class:icon-only={props.iconOnly}
  >
    {@render props.children()}
  </button>
{/if}

<style lang="scss">
  @use 'sass:color';
  @use '~/theme/theme';
  @use '~/theme/include-media' as *;

  a.action {
    text-decoration: none;
  }
  .action {
    $color: hsl(213, 56%, 71%);
    transition: all 0.25s ease-in;
    cursor: pointer;
    font-weight: bold;
    color: #2f3640;
    background-color: color.scale($color, $lightness: 80%);
    line-height: 1em;
    border-radius: 999px;
    border: 2px solid #2f3640;
    text-transform: uppercase;
    margin: 0;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 0.5em;
    -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
    -moz-box-sizing: border-box; /* Firefox, other Gecko */
    box-sizing: border-box; /* Opera/IE 8+ */
    &:hover {
      background-color: color.scale($color, $lightness: 40%);
      color: rgb(3, 42, 95);
      border-color: rgb(3, 42, 95);
    }
    &:active {
      background-color: color.scale($color, $lightness: 20%);
    }
    &:disabled {
      cursor: initial;
      color: rgba(3, 42, 95, 0.5);
      border-color: rgba(3, 42, 95, 0.25);
      &:hover {
        background-color: color.scale($color, $lightness: 80%);
        color: rgba(3, 42, 95, 0.5);
        border-color: rgba(3, 42, 95, 0.25);
      }
    }
    @include media('>=tablet') {
      font-size: 16px;
    }
    :global(html.dark-mode) & {
      $color: #2f3640;
      color: white;
      background-color: $color;
      border-color: white;
      &:hover {
        background-color: color.scale($color, $lightness: 10%);
      }
      &:active {
        background-color: color.scale($color, $lightness: 20%);
      }
      &:disabled {
        cursor: initial;
        color: rgba(255, 255, 255, 0.5);
        border-color: rgba(255, 255, 255, 0.25);
        &:hover {
          background-color: $color;
          color: rgba(255, 255, 255, 0.5);
          border-color: rgba(255, 255, 255, 0.25);
        }
      }
      &.primary {
        $color: theme.$tile-blue-dark;
        border-color: color.scale($color, $lightness: 20%);
        background-color: color.scale($color, $lightness: -20%);
        &:hover {
          border-color: color.scale($color, $lightness: 40%);
          background-color: color.scale($color, $lightness: 0%);
        }
      }
    }
    // TODO
    & :global(svg) {
      width: 24px;
      height: 24px;
      @include media('<=360px') {
        width: 20px;
        height: 20px;
      }
    }
    &.primary {
      $color: #c4e0e3;
      border-color: color.scale($color, $lightness: -20%);
      background-color: color.scale($color, $lightness: 20%);
      &:hover {
        border-color: color.scale($color, $lightness: -40%);
        background-color: color.scale($color, $lightness: 10%);
      }
    }
    &.icon-only {
      font-size: 0;
      padding: 8px;
    }
    &.large {
      border-radius: 1em;
      gap: 1em;
      padding: 0.75em 1em;
    }
  }
</style>
